import React, { FC, useState } from 'react';
import { Tabs, Tab, Box, Typography, IconButton } from '@mui/material';
import { useAppTheme } from 'theme/useAppTheme';

import { OpenIcon } from 'assets/icons';
import { useAppNavigate } from 'hooks';

export interface IWiseTabItem {
  label: string;
  children: React.ReactNode;
  route?: string;
  subtitle?: React.ReactNode;
}

export interface IWiseTabsProps {
  label?: string;
  tabs: IWiseTabItem[];
  defaultTabIndex?: number;
  className?: string;
  centered?: boolean;
}

interface ITabLabelProps {
  tabLabel: string;
  route?: string;
  subtitle?: React.ReactNode;
}

const TabLabel: FC<ITabLabelProps> = ({ tabLabel, route, subtitle }) => {
  const navigate = useAppNavigate();

  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (route) {
      navigate(route);
    }
  };

  return (
    <Box sx={{ textAlign: 'left', width: '100%', py: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography component="span" variant="body2" sx={{ fontWeight: 'inherit' }}>
          {tabLabel}
        </Typography>
        {route && (
          <IconButton
            component="span"
            onClick={handleIconClick}
            size="small"
            sx={{ p: 0.25 }}
            aria-label="Otwórz"
          >
            <OpenIcon style={{ width: 16, height: 16 }} />
          </IconButton>
        )}
      </Box>
      {subtitle != null && subtitle !== '' && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

const WiseTabs: FC<IWiseTabsProps> = ({
  label,
  tabs,
  defaultTabIndex = 0,
  className,
  centered = false
}) => {
  const theme = useAppTheme();
  const [value, setValue] = useState(defaultTabIndex);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  if (!tabs?.length) {
    return null;
  }

  return (
    <Box
      className={className}
      sx={{
        backgroundColor: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0px 2px 5px rgba(6, 25, 56, 0.06)'
      }}>
      <Box sx={{ px: 2, pt: 2 }}>
        {label && (
          <Typography
            component="h2"
            variant="subtitle1"
            sx={{ fontWeight: 600, mb: 1.5, fontSize: 24, color: theme.palette.app.textPrimary }}>
            {label}
          </Typography>
        )}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="wise-tabs"
            variant="fullWidth"
            centered={centered}
            textColor="secondary"
            indicatorColor="secondary"
            sx={{
              '& .Mui-selected': {
                fontWeight: 700
              }
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={
                  <TabLabel
                    tabLabel={tab.label}
                    route={tab.route}
                    subtitle={tab.subtitle}
                  />
                }
                id={`wise-tab-${index}`}
                aria-controls={`wise-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>
      </Box>
      {tabs.map((tab, index) => (
        <div
          key={index}
          role="tabpanel"
          hidden={value !== index}
          id={`wise-tabpanel-${index}`}
          aria-labelledby={`wise-tab-${index}`}
        >
          {value === index && <Box sx={{ px: 2, py: 2 }}>{tab.children}</Box>}
        </div>
      ))}
    </Box>
  );
};

export default WiseTabs;
